import { Button } from "@components/ui/Button"
import { Icon } from "@components/ui/Icon"
import { useMotionSize } from "@hooks/useMotionSize"
import * as D from "@radix-ui/react-dialog"
import { cn, cx, tv } from "@utils/tw"
import { EditableText } from "eddev/blocks"
import { useHydrating, useRoute, useRouter } from "eddev/routing"
import { motion, useSpring, useTransform } from "motion/react"
import { useEffect, useRef, useState } from "react"
import { SFFRagDocumentTypeName } from "../../server/search/rag"
import { SearchInput } from "./SearchInput"
import { SearchResultsDisplay } from "./SearchResultsDisplay"
import { useSearchResults } from "./useSearchResults"
import { useSearch } from "./useSearchInput"

type Props = {}

export function SearchBoxDesktop(props: Props) {
  const hydrating = useHydrating()

  const ref = useRef<HTMLInputElement>(null!)
  const resultContainer = useRef<HTMLDivElement | null>(null)

  const router = useRouter()
  const route = useRoute()

  const isSearchPage = route.view === "search-results"

  const [types, setTypes] = useState<SFFRagDocumentTypeName[]>([])
  const [focused, setFocused] = useState(false)
  const [isOpen, setIsOpen] = useState(false)

  const search = useSearch()

  // Handle open state
  useEffect(() => {
    setIsOpen(focused && !isSearchPage)
  }, [focused, isSearchPage])

  const close = () => {
    setFocused(false)
    ref.current.blur()
  }

  // For debugging
  // useEffect(() => {
  //   setIsOpen(true)
  // })

  // Close on Escape key
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        close()
      }
    }
    window.addEventListener("keydown", onKeyDown)
    return () => {
      window.removeEventListener("keydown", onKeyDown)
    }
  }, [])

  // Close on route change
  useEffect(() => {
    close()
  }, [route.pathname])

  // Get search results
  const searchResults = useSearchResults({
    enabled: !isSearchPage,
    isMobile: false,
    isQuickSearch: true,
    types: types,
  })

  // Dialog measurements/height
  const [contentRef, contentRect] = useMotionSize()
  const [inputWrapperRef, inputRect] = useMotionSize()
  const dialogTargetHeight = useTransform(
    [contentRect.height, inputRect.height],
    ([contentHeight, inputHeight]: number[]) => {
      if (!contentRect.ready) return 0
      if (isOpen) {
        return contentHeight
      } else {
        return inputHeight
      }
    },
  )
  const dialogSpring = useSpring(dialogTargetHeight, {
    stiffness: 300,
    damping: 40,
    restDelta: 0.001,
    restSpeed: 0.001,
  })
  const dialogHeight = useTransform(dialogSpring, (value) => {
    return Math.max(inputRect.height.get(), value)
  })

  // Styles
  const styles = createStyles({
    focused: focused || search.input.inputQuery.length > 0,
    hasValue: search.input.inputQuery.length > 0,
    open: isOpen,
    loading: search.loading,
  })

  return (
    <D.Root open={isOpen} onOpenChange={setFocused} modal>
      <motion.div
        className={cx(
          "fixed inset-0 bg-[#000000] z-[49] will-change-transform",
          isOpen ? "!pointer-events-auto" : "!pointer-events-none",
        )}
        initial={{ opacity: 0 }}
        animate={{ opacity: isOpen ? 0.5 : 0 }}
        transition={{ duration: 0.2 }}
        onPointerDown={() => {
          setFocused(false)
          setIsOpen(false)
        }}
      />
      <div>
        <motion.div
          className={styles.base()}
          onSubmit={(e) => {
            if (env.admin) return
            e.preventDefault()
            router.navigate("/search/?q=" + encodeURIComponent(search.input.inputQuery))
          }}
          data-mixpanel-area="Search Panel"
        >
          <div className="outline-none">
            <motion.div
              variants={{
                open: {
                  width: "calc(max(490px, 34vw)",
                  borderRadius: 16,
                  // height: "var(--contents-height, auto)",
                  marginTop: "calc(var(--spacing-headerRowHeight) / 2 - var(--size-control) / 2)",
                },
                closed: {
                  borderRadius: 8,
                  width: "calc(max(320px, 24vw))",
                  // height: "var(--size-control)",
                  marginTop: "calc(var(--spacing-headerRowHeight) / 2 - var(--size-control) / 2)",
                },
              }}
              initial="closed"
              animate={isOpen ? "open" : "closed"}
              className={styles.dialog()}
              transition={{
                type: "spring",
                stiffness: 300,
                damping: 40,
                restDelta: 0.001,
                restSpeed: 0.001,
              }}
              style={{
                height: hydrating ? "auto" : dialogHeight,
              }}
            >
              <motion.div ref={contentRef} className="relative">
                <motion.div variants={{ open: { padding: 14, paddingBottom: 0 }, closed: { padding: 0 } }}>
                  {/* Input container */}
                  <motion.div className={styles.inputContainer()} ref={inputWrapperRef}>
                    <Icon name="search" />
                    <SearchInput
                      ref={ref}
                      className={styles.input()}
                      onFocus={() => {
                        setFocused(true)
                      }}
                      openFullResults
                      resultContainer={resultContainer}
                    />
                    <motion.div>
                      <Icon name="load" className={styles.spinner()} />
                    </motion.div>
                    <EditableText
                      className={cn(styles.placeholder(), env.admin ? "!opacity-100 !pointer-events-auto" : "")}
                      store="searchPlaceholder"
                      defaultValue="Search films, venues, guests..."
                    />
                  </motion.div>
                </motion.div>
                {/* Search results */}
                {!isSearchPage && (
                  <motion.div
                    ref={resultContainer}
                    className="flex flex-col small-results mt-2"
                    variants={{ open: { opacity: 1, paddingBottom: 0 }, closed: { opacity: 0, paddingBottom: 14 } }}
                  >
                    {/* Primary results */}
                    <div className="px-[18px] scrollbars-hidden max-h-[calc(100vh-140px)] overflow-y-auto overscroll-contain">
                      <SearchResultsDisplay types={types} setTypes={setTypes} search={searchResults} isSmall />
                      <div className="h-[8px] flex-none" />
                    </div>
                    {/* Link to all results */}
                    {search.input.inputQuery.length > 1 && (
                      <div className="border-t border-bg-low flex flex-col px-[8px] py-[8px] flex-none">
                        <Button
                          href={`/search/?q=${encodeURIComponent(search.input.inputQuery.trim())}`}
                          variant="ghost"
                          className={cn("!pl-[10px] !pr-[12px] justify-between !border-none")}
                          rightIcon="chevronRight"
                        >
                          {`View all results for '${search.input.inputQuery.trim()}'`}
                        </Button>
                      </div>
                    )}
                  </motion.div>
                )}
              </motion.div>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </D.Root>
  )
}

const createStyles = tv({
  base: "w-full justify-center",
  slots: {
    dialog: [
      "justify-center relative transition-colors z-50 h-control min-h-control overflow-clip will-change-transform transform-gpu outline-none",
    ],
    inputContainer: [
      "flex relative px-3 lg:px-2 items-center rounded-md flex-1 m-0",
      "bg-bg-mid/60",
      "transition-colors",
      "cursor-pointer",
    ],
    input: [
      "max-sm:[font-size:16px] p-2 max-md:h-control appearance-none bg-transparent border-none flex-1 ring-none h-control",
      "cursor-pointer outline-none",
    ],
    placeholder: [
      "text-fg absolute inset-0 flex items-center justify-center px-3 text-nowrap pointer-events-none",
      "opacity-1 transition-opacity",
    ],
    spinner: "transition-opacity opacity-0 animate-spin",
  },
  variants: {
    focused: {
      true: {
        root: "bg-bg-mid/80 cursor-text",
        input: "cursor-text",
      },
    },
    hasValue: {
      true: {
        input: "cursor-text",
        placeholder: "opacity-0",
      },
    },
    open: {
      true: {
        dialog: "bg-bg shadow-popover",
      },
    },
    loading: {
      true: {
        spinner: "opacity-100",
      },
    },
  },
})
